/**
 * #813 (RBAC #533 étape 2) — RÉFÉRENTIEL UNIQUE des rôles legacy.
 * ================================================================
 *
 * Source de vérité partagée par :
 * - les routes backoffice (`backoffice.routes.ts`, `data.roles`) — fallback
 *   legacy quand le flag RBAC est off ou le module non couvert par la matrice ;
 * - la visibilité des sections du menu (`PermissionService.canAccessModule`,
 *   mode legacy) via `MODULE_LEGACY_MENU_ROLES` ;
 * - le seed des règles ACL par défaut (`RbacService.seedLegacyAclRules`) via
 *   `MODULE_ROUTE_ROLES` : activer le flag après seed reproduit le legacy,
 *   qu'on affine ensuite dans la matrice (rbac-settings).
 *
 * La vérité RUNTIME quand le flag est actif = la matrice `rbac_acl` ; ces
 * constantes ne sont alors plus consultées pour les modules couverts.
 *
 * `super_admin` n'est jamais listé : il est court-circuité dans les guards
 * et dans `RbacService.can()`.
 */

import { RbacModule } from '../layout/nav-sections';

/** Écrans d'administration destructifs (seed, config plateforme, RBAC lui-même). */
export const ADMIN_ROLES = ['admin', 'topManager'];

/** Logistique Belgique + transit : pas d'agent customs/delivery sur la réception. */
export const LOGISTICS_ROLES = ['admin', 'topManager', 'manager', 'warehouse', 'transitory'];

/** Distribution Congo (livraison finale) : inclut en plus les partenaires livreurs. */
export const DELIVERY_ROLES = ['admin', 'topManager', 'manager', 'warehouse', 'transitory', 'delivery'];

/**
 * #921 — ÉDITION du registre des livreurs RDC (`partners/new`, `partners/:id`).
 *
 * Décision produit du 2026-07-28 : créer ou modifier une fiche livreur (nom,
 * type, zones couvertes, téléphone, email, adresse, véhicule, statut
 * actif/suspendu, interne/externe, localisation) est un acte de RESPONSABLE.
 * Question posée au user pour `warehouse`, `transitory` et `delivery` :
 * « aucune de ces personnes pour le moment ».
 *
 * ⚠️ La CONSULTATION reste `DELIVERY_ROLES` (route `partners`, la liste) : toute
 * la chaîne de livraison doit pouvoir VOIR qui sont les livreurs et leurs zones.
 * Seuls les responsables les modifient. Miroir exact côté serveur — la rule
 * `deliveryPartners` lit en `isEmployee()` et écrit en `isManager()`.
 *
 * ⚠️ NE PAS remplacer `DELIVERY_ROLES` par ce jeu : cette constante garde aussi
 * `deliveries`/`deliveries/:id` (le travail de livraison lui-même, où le rôle
 * `delivery` DOIT rester) et alimente `MODULE_ROUTE_ROLES.logistics_africa`,
 * base du seed de la matrice ACL. L'union du module reste donc inchangée,
 * puisque la liste `partners` porte toujours `DELIVERY_ROLES`.
 */
export const PARTNER_REGISTRY_ROLES = ['admin', 'topManager', 'manager'];

/** Finance / marketing / monitoring / CRM : pas d'accès entrepôt au P&L, payouts, promos. */
export const FINANCE_ROLES = ['admin', 'topManager', 'manager'];

/** RH (aligné lot T20 : routes RH restreintes manager+/rh). */
export const HR_ROLES = ['admin', 'rh', 'manager', 'topManager'];

/** Customer service : management + RH (agents CS). */
export const CS_ROLES = ['admin', 'topManager', 'manager', 'rh'];

/**
 * #2050 — écrans du GUICHET du service client (tableau CS, tickets) : CS_ROLES
 * plus l'agent `support`. Pas `cs-settings`, ni les notifications en échec, ni
 * l'envoi assisté, qui restent sur leurs listes. Le support n'est PAS dans
 * MODULE_ROUTE_ROLES.customer_service : le seed de la matrice ne lui donne donc
 * aucun sous-module CS — drapeau RBAC allumé, ses droits CS se posent à la
 * main dans rbac-settings (la prod démarre drapeau éteint).
 */
export const CS_DESK_ROLES = [...CS_ROLES, 'support'];

/** IT (ITIL, catalogue de services, CMDB, assets, fournisseurs) : management. */
export const IT_ROLES = ['admin', 'topManager', 'manager'];

/** Virtual Mailbox : entrepôt + management. */
export const VM_ROLES = ['admin', 'topManager', 'manager', 'warehouse'];

/** Base de connaissances : tout le staff sauf customs. */
export const KNOWLEDGE_ROLES = ['admin', 'topManager', 'manager', 'rh', 'warehouse', 'transitory', 'delivery', 'support'];

/** #358 — envoi assisté : aligné sur le tier 'employee' de la CF createAssistedShipment. */
export const ASSISTED_ROLES = ['admin', 'rh', 'warehouse', 'manager', 'topManager'];

/**
 * Accès ROUTES legacy par module (union des `data.roles` du module).
 * Sert de base au seed ACL (`seedLegacyAclRules`).
 */
export const MODULE_ROUTE_ROLES: Record<RbacModule, string[]> = {
  logistics_europe: LOGISTICS_ROLES,
  logistics_africa: DELIVERY_ROLES,
  virtual_mailbox: VM_ROLES,
  hr: HR_ROLES,
  it: IT_ROLES,
  finance: FINANCE_ROLES,
  customer_service: CS_ROLES,
  marketing: FINANCE_ROLES,
  knowledge: KNOWLEDGE_ROLES,
  monitoring: FINANCE_ROLES,
  crm: FINANCE_ROLES,
  admin: ADMIN_ROLES,
};

/**
 * #935 — SEED ACL : exceptions par SOUS-MODULE.
 *
 * `seedLegacyAclRules()` seede par défaut chaque sous-module avec l'UNION des
 * rôles de son module (`MODULE_ROUTE_ROLES`). Une route plus restrictive que
 * cette union s'élargit donc silencieusement le jour où le drapeau RBAC passe
 * à `true` (#815) — le `data.roles` de la route n'est alors plus consulté du
 * tout (`PermissionService.routeAllowed`).
 *
 * Cette table est le remède : elle fige, pour un sous-module donné, le jeu de
 * rôles réellement porté par sa route. La bascule du drapeau redevient un
 * non-événement pour ces écrans.
 *
 * ⚠️ La clé est un **sous-module** (`routeToKey(route)`), pas une route : le
 * sous-module couvre aussi les routes ENFANTS résolues par préfixe. Quand une
 * route enfant doit porter un jeu PLUS restrictif que son préfixe (l'édition
 * du registre des livreurs vs la liste `partners`), on lui crée un sous-module
 * SYNTHÉTIQUE : mapping explicite `data.rbac` sur la route + entrée dans
 * `RBAC_SUBMODULES` + seed ici (`partners_registry`, #935). La garde composant
 * (`canManageDeliveryPartner`, #934) reste en défense en profondeur.
 *
 * L'écart figé par le test `backoffice-rbac-flag-divergence.spec.ts` est la
 * mesure de ce qui reste à arbitrer.
 */
export const SUBMODULE_SEED_ROLES: Record<string, string[]> = {
  /** #358 — aligné sur le tier 'employee' de la CF createAssistedShipment. */
  envoi_assiste: ASSISTED_ROLES,
  /**
   * #585/#935 — la configuration de la base de connaissances (approbation,
   * tags) est un acte de management, alors que le module `knowledge` est
   * ouvert à tout le staff sauf `customs`. Sans cette entrée, la bascule du
   * drapeau l'ouvrait à `rh`, `warehouse`, `transitory` et `delivery`.
   */
  knowledge_settings: FINANCE_ROLES,
  /**
   * #935 — réception arrivée, dégroupage et tri livraison : les routes portent
   * LOGISTICS_ROLES (sans `delivery`) alors que le module `logistics_africa`
   * est seedé DELIVERY_ROLES. Sans ces entrées, la bascule du drapeau ouvrait
   * ces écrans d'ÉCRITURE (aucune garde composant) aux partenaires livreurs —
   * l'écart 🔴 de l'audit #935. `degroupage` couvre aussi
   * `degroupage/:containerId` (résolution par préfixe). Le travail de
   * livraison lui-même (`deliveries*`) garde DELIVERY_ROLES via l'union du
   * module — `canAny('logistics_africa')` reste donc vrai pour `delivery`.
   */
  /**
   * #1819 — décision du propriétaire (20/10/2026) : le rôle `delivery` devient
   * la « Logistique RDC » (personnel de Kinshasa) et fait réception, pesée,
   * dégroupage et tri au Congo. Il reste hors de la logistique Europe
   * (LOGISTICS_ROLES) et de toute fonction financière. L'exclusion de #935
   * visait des partenaires livreurs : aucun compte `delivery` n'existait.
   */
  congo_reception: DELIVERY_ROLES,
  degroupage: DELIVERY_ROLES,
  sorting: DELIVERY_ROLES,
  /**
   * #935 (dernier écart) — l'ÉDITION du registre des livreurs est un acte de
   * responsable (#921, décision produit du 28/07). Sous-module synthétique
   * porté par le `data.rbac` explicite de `partners/new` / `partners/:id` ;
   * la LISTE `partners` reste, elle, seedée DELIVERY_ROLES via l'union du
   * module.
   */
  partners_registry: PARTNER_REGISTRY_ROLES,
};

/**
 * Visibilité MENU legacy par module (flag off ou module non couvert).
 * Deux écarts assumés vs `MODULE_ROUTE_ROLES` :
 * - `logistics_africa` : le menu ne s'affiche pas pour `transitory` (#535),
 *   mais les routes du module lui restent accessibles (cohérence intra-module
 *   avec les partenaires livraison) ;
 * - `customer_service` : `warehouse` accède à l'envoi assisté par URL/route
 *   (#358) sans voir la section CS du menu.
 * `crm` était absent du switch historique (masqué pour manager alors que les
 * routes l'autorisent — écart corrigé ici, aligné FINANCE_ROLES).
 */
export const MODULE_LEGACY_MENU_ROLES: Record<RbacModule, string[]> = {
  ...MODULE_ROUTE_ROLES,
  logistics_africa: ['admin', 'topManager', 'manager', 'warehouse', 'delivery'],
  // #2050 — la section Customer Service s'affiche aussi pour le support.
  customer_service: CS_DESK_ROLES,
};
